import axios from 'axios';
import CONFIG from '../config/config';

/**
 * Fetch the list of users.
 * @param {string} subscriptionGuid - The unique identifier for the subscription.
 * @returns {Promise<Array>} - List of users.
 */
export const fetchUsers = async (subsGuid) => {
    try {
        const url = `${CONFIG.API.BASE_URL}/users/list`;
        const response = await axios.get(url, {
            headers: {
                ...CONFIG.DEFAULT_HEADERS,
                'accessToken': CONFIG.API.ACCESS_TOKEN,
                'subscriptionGuid': subsGuid,
            },
        });

        if (response.status === 200) {
            return response.data; // User records
        } else {
            console.error(`Error fetching users: ${response.status} - ${response.data}`);
            throw new Error(`Error: ${response.status}`);
        }
    } catch (error) {
        if (error.response && error.response.status === 404) {
            console.warn('No users found, returning empty array.');
            return [];
        }

        console.error('Error fetching users:', error);
        throw error; // Re-throw the error for other status codes
    }
};

/**
 * Fetch a user by their email.
 * @param {string} email - The email address of the user.
 * @returns {Promise<Object>} - The user data if found.
 */
export const getUserByEmail = async (email) => {
    try {
        const url = `${CONFIG.API.BASE_URL}/users/getByEmail`;
        const response = await axios.get(url, {
            params: { email },
            headers: {
                ...CONFIG.DEFAULT_HEADERS,
                accessToken: CONFIG.API.ACCESS_TOKEN,
            },
        });

        if (response.status === 200) {
            return response.data.user;
        } else {
            throw new Error(`Error: ${response.status} - ${response.data.message}`);
        }
    } catch (error) {
        console.error('Error fetching user by email:', error);
        throw error;
    }
};

/**
 * Fetch a user by their GUID.
 * @param {string} userGuid - The unique identifier for the user.
 * @returns {Promise<Object>} - The user data if found.
 */
export const getUserByGuid = async (userGuid) => {
    try {
        if (!userGuid || typeof userGuid !== 'string') {
            throw new Error('Invalid or empty userGuid provided');
        }

        const url = `${CONFIG.API.BASE_URL}/users/getByGuid`;
        const response = await axios.get(url, {
            params: { userGuid },
            headers: {
                ...CONFIG.DEFAULT_HEADERS,
                accessToken: CONFIG.API.ACCESS_TOKEN,
            },
        });

        if (response.status === 200) {
            return response.data.user;
        } else {
            throw new Error(`Error: ${response.status} - ${response.data.message}`);
        }
    } catch (error) {
        console.error('Error fetching user by GUID:', error);
        throw error;
    }
};

/**
 * Create a new user.
 * @param {Object} userData - The data for the new user.
 * @param {string} subscriptionGuid - The subscription GUID for the user.
 * @returns {Promise<Object>} - The created user data.
 */
export const createUser = async (userData, subscriptionGuid) => {
    console.log('Creating user with data:', JSON.stringify(userData, null, 2));
    try {
        const url = `${CONFIG.API.BASE_URL}/users/create`;
        const response = await axios.post(url, userData, {
            headers: {
                ...CONFIG.DEFAULT_HEADERS,
                'accessToken': CONFIG.API.ACCESS_TOKEN,
                'userGuid': userData.user.userGuid,
            },
        });

        if (response.status === 201) { // HTTP 201 for Created
            return response.data; // Created user data
        } else {
            console.error(`Error creating user: ${response.status} - ${response.data}`);
            throw new Error(`Error: ${response.message}`); // ${response.status}
        }
    } catch (error) {
        if (error.response && error.response.status === 409) {
            // Capture the API message for duplicate email
            throw new Error(error.response.data.message);
        }
        throw new Error('Failed to create user');
    }
};

/**
 * Update an existing user.
 * @param {string} userGuid - The unique identifier for the user to be updated.
 * @param {Object} updatedFields - The fields to update in the user record.
 * @returns {Promise<Object>} - The updated user data.
 */
export const updateUser = async (userGuid, updatedFields) => {
    try {
        const url = `${CONFIG.API.BASE_URL}/users/updateFields`;
        const response = await axios.patch(url, { updatedFields }, {
            headers: {
                ...CONFIG.DEFAULT_HEADERS,
                'accessToken': CONFIG.API.ACCESS_TOKEN,
                'userGuid': userGuid,
            },
        });

        if (response.status === 200) {
            return response.data; // Updated user data
        } else {
            console.error(`Error updating user: ${response.status} - ${response.data}`);
            throw new Error(`Error: ${response.status}`);
        }
    } catch (error) {
        console.error('Error updating user:', error);
        throw error;
    }
};